/**
 * subscriber.ts — decode an incoming stream's binary frames (VP8) and paint
 * the resulting VideoFrames onto a <canvas>. Waits for a keyframe before
 * feeding the decoder (a delta before the first key is undecodable).
 */
import { decodeHeader, payloadOf, TRACK_VIDEO, TYPE_KEY } from './protocol.js';

export interface SubscriberOptions {
    streamId: number;
    codec: string;       // 'vp8'
    width: number;
    height: number;
    canvas: HTMLCanvasElement;
    /** Called when we need a keyframe (no key seen yet) so the caller can ask the server. */
    onNeedKeyframe?: (streamId: number) => void;
}

export class Subscriber {
    private decoder: VideoDecoder | null = null;
    private ctx: CanvasRenderingContext2D | null = null;
    private gotKeyframe = false;
    private askedForKey = false;
    private stopped = false;
    private readonly opts: SubscriberOptions;

    constructor(options: SubscriberOptions) {
        this.opts = options;
        this.ctx = options.canvas.getContext('2d');
    }

    start(): void {
        this.decoder = new VideoDecoder({
            output: (frame) => this.onFrame(frame),
            error: (e) => { console.error('[vidchat] decoder error', e); },
        });
        this.decoder.configure({ codec: this.opts.codec, codedWidth: this.opts.width, codedHeight: this.opts.height });
    }

    /** Feed a raw binary media frame (header + encoded payload). */
    feed(data: Uint8Array): void {
        if (this.stopped || !this.decoder) return;
        const h = decodeHeader(data);
        if (!h || h.track !== TRACK_VIDEO) return; // audio handled separately later

        const isKey = h.type === TYPE_KEY;
        if (!this.gotKeyframe) {
            if (!isKey) {
                // can't decode deltas before a key; nudge the server once
                if (!this.askedForKey) { this.askedForKey = true; this.opts.onNeedKeyframe?.(this.opts.streamId); }
                return;
            }
            this.gotKeyframe = true;
        }

        const payload = payloadOf(data);
        // copy out of the WS buffer (it may be reused) into a standalone chunk
        const bytes = payload.slice();
        try {
            const chunk = new EncodedVideoChunk({
                type: isKey ? 'key' : 'delta',
                timestamp: h.timestamp,
                data: bytes,
            });
            if (this.decoder.state === 'configured') this.decoder.decode(chunk);
        } catch (e) {
            console.error('[vidchat] decode threw', e);
        }
    }

    private onFrame(frame: VideoFrame): void {
        if (this.stopped || !this.ctx) { frame.close(); return; }
        const cv = this.opts.canvas;
        // size the canvas to the frame on first paint / size change
        if (cv.width !== frame.displayWidth || cv.height !== frame.displayHeight) {
            cv.width = frame.displayWidth;
            cv.height = frame.displayHeight;
        }
        try { this.ctx.drawImage(frame, 0, 0, cv.width, cv.height); }
        catch (e) { console.error('[vidchat] drawImage threw', e); }
        frame.close();
    }

    stop(): void {
        this.stopped = true;
        try { if (this.decoder && this.decoder.state !== 'closed') this.decoder.close(); } catch { /* ignore */ }
        this.decoder = null;
    }
}
